import { reportUsage } from '@ms/yammer-telemetry';
import { isMac } from '@ms/yammer-web-core-ui/dist/util/browserDetector';
import { registerBackButtonHandler } from '@ms/yammer-web-support/dist/metaOsHub';
import { useIsDesktopClient, useIsTeamsTab } from 'hooks/selectors';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useIsDeepLinkPage } from './useIsDeepLinkPage';

type UseBackButtonProps = () => {
  readonly goBack: () => void;
  readonly showButton: boolean;
};

const rootPaths = [
  /^\/admin\/?$/,
  /^\/articles\/new$/,
  /^\/discover-groups\/explore((\/\w+)+|\/?)$/,
  /^\/feed((\/\w+)+|\/?)$/,
  /^\/inbox((\/\w+)+|\/?)$/,
  /^\/leaders\/?$/,
  /^\/leadership-corner((\/\w+)+|\/?)$/,
  /^\/storylines((\/\w+)+|\/?)$/,
  /^\/questions\/?$/,
  /^\/user-settings((\/\w+)+|\/?)$/,
  /^\/events\/[^/]+(\/questions|\/unanswered)?$/,
];

export const useBackButtonProps: UseBackButtonProps = () => {
  const history = useHistory();
  const location = useLocation();
  const [showButton, setShowButton] = useState(false);
  const isDeepLinkPage = useIsDeepLinkPage(location);
  const hasHistory = history.length > 1;
  const isTeamsTab = useIsTeamsTab();
  const isDesktop = useIsDesktopClient();

  // on a Teams channel tab, the back button stays hidden on the root page; it appears once the
  // user has navigated within the app, at which point the history state carries a prevPathname
  const shouldHideBackButtonOnTeamsTab = isTeamsTab && !window.history.state?.state && hasHistory;

  const goBack = useCallback(() => {
    reportUsage({
      eventName: 'viva_engage_back_button_clicked',
      eventProperties: { backFrom: window.location.href },
    });
    history.goBack();
  }, [history]);

  useEffect(() => {
    if (isDeepLinkPage || !hasHistory || shouldHideBackButtonOnTeamsTab) {
      setShowButton(false);

      return;
    }

    const path = location.pathname;
    if (rootPaths.some((rootPath) => rootPath.test(path))) {
      setShowButton(false);
    } else {
      setShowButton(true);
    }
  }, [location, isDeepLinkPage, hasHistory, shouldHideBackButtonOnTeamsTab]);

  useEffect(() => {
    if (!showButton) {
      return;
    }

    // 1. Teams UI back button
    registerBackButtonHandler(() => {
      history.goBack();

      return true;
    });

    // 2. Keyboard shortcuts (Desktop only)
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isDesktop || e.key !== 'ArrowLeft') {
        return;
      }

      const isMacOs = isMac();
      const isBackShortcut = (isMacOs && e.metaKey) || (!isMacOs && e.altKey);

      if (isBackShortcut) {
        history.goBack();
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [showButton, history, isDesktop]);

  return {
    goBack,
    showButton,
  };
};
